import { Auth } from "@saasbase-io/loginflow-websdk";
import { LitElement, html, nothing } from "lit";
import { property, state } from "lit/decorators.js";
import { cn, getInitials, waitForAuthConfigured } from "@/utils";
import { TemplateResult } from "lit";

type MenuItem = {
  id?: string;
  label: string;
  href?: string;
  onClick?: (e: Event, item?: MenuItem) => void;
  icon?: TemplateResult | string;
};

// ==================== USER SNIPPET ====================
class SbUserSnippet extends LitElement {
  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  @property({ type: Boolean }) isTrigger = false;
  @property({ type: Object }) user = {};

  render() {
    // @ts-ignore - user shape is dynamic
    const { name, email, avatar_url } = this.user || {};

    return html`
      <div
        class=${cn(
          "sb-flex sb-items-center sb-gap-2 sb-w-full",
          this.isTrigger ? "sb-py-2" : "",
        )}
      >
        <rtg-avatar size="lg">
          <rtg-avatar-image src=${avatar_url}></rtg-avatar-image>
          <rtg-avatar-fallback
            >${getInitials(name || email)}</rtg-avatar-fallback
          >
        </rtg-avatar>

        <div class="sb-min-w-0 sb-flex-1 sb-text-left">
          ${name
            ? html`<rtg-typography-p part="user-name"
                >${name}</rtg-typography-p
              >`
            : ""}
          ${email
            ? html`<rtg-typography-muted class="sb-truncate"
                >${email}</rtg-typography-muted
              >`
            : ""}
        </div>
      </div>
    `;
  }
}

customElements.define("sb-user-snippet", SbUserSnippet);

// ==================== NAV USER ====================
export class SbNavUser extends LitElement {
  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  // New property
  @property({ type: String }) accountSettingsPath = "";
  @property({ attribute: false }) menuItems: MenuItem[] = [];

  @state() private user: any = {};
  @state() private signedIn = false;
  @state() private isLoading = true;

  connectedCallback() {
    super.connectedCallback();
    void this.loadUser();
  }

  private tryRemoveFallback() {
    requestAnimationFrame(() => {
      const root = this as HTMLElement;

      const fallback = root.querySelector('[part="fallback"]');

      if (fallback) {
        fallback.remove();
      } else {
        console.warn("[sb-signup] fallback not found yet");
      }
    });
  }

  private async loadUser() {
    this.isLoading = true;

    try {
      await waitForAuthConfigured(Auth);
      const isAuth = Auth.isAuthenticated();
      this.signedIn = isAuth;

      if (isAuth) {
        const authenticatedUser = Auth.getAuthenticatedUser();

        this.user = {
          email: authenticatedUser?.email,
          name:
            authenticatedUser?.name ||
            `${authenticatedUser?.given_name || ""} ${authenticatedUser?.family_name || ""}`.trim(),
          avatar_url: authenticatedUser?.picture,
        };
      }
    } catch (err) {
      console.error("[sb-nav-user] Failed to load user:", err);
      this.signedIn = false;
    } finally {
      this.isLoading = false;
      this.tryRemoveFallback();
    }
  }

  render() {
    // Render nothing while loading or when not signed in
    if (this.isLoading || !this.signedIn) {
      return html``;
    }

    return html`
      <rtg-dropdown-menu>
        <rtg-dropdown-menu-trigger as-child>
          <rtg-button part="trigger-button" variant="secondary" size="lg">
            <sb-user-snippet
              .isTrigger=${true}
              .user=${this.user}
            ></sb-user-snippet>
          </rtg-button>
        </rtg-dropdown-menu-trigger>

        <rtg-dropdown-menu-content class="sb-w-64">
          <!-- User header -->
          <rtg-dropdown-menu-group>
            <div class="sb-px-2">
              <sb-user-snippet .user=${this.user}></sb-user-snippet>
            </div>
          </rtg-dropdown-menu-group>

          <rtg-dropdown-menu-separator></rtg-dropdown-menu-separator>

          <rtg-dropdown-menu-group>
            <rtg-dropdown-menu-item @click=${this._handleAccountSettings}>
              Account settings
            </rtg-dropdown-menu-item>

            <!-- Render only rtg-dropdown-menu-item elements from menuItems JS property. -->
            ${this.menuItems && this.menuItems.length
              ? this.menuItems.map(
                  (item) => html`
                    <rtg-dropdown-menu-item
                      @click=${(e: Event) => this._handleMenuItemClick(e, item)}
                      data-id=${item.id || ""}
                    >
                      ${typeof item.icon === "string"
                        ? html`${item.icon}`
                        : item.icon}
                      ${item.label}
                    </rtg-dropdown-menu-item>
                  `,
                )
              : null}
          </rtg-dropdown-menu-group>

          <rtg-dropdown-menu-group>
            <rtg-dropdown-menu-item @click=${this._handleLogout}>
              Log out
            </rtg-dropdown-menu-item>
          </rtg-dropdown-menu-group>
        </rtg-dropdown-menu-content>
      </rtg-dropdown-menu>
    `;
  }

  private _navigate(path: string) {
    console.log("Navigate to:", path);
    window.location.href = path;
  }
  private _handleAccountSettings = () => {
    this.dispatchEvent(
      new CustomEvent("account-settings", { bubbles: true, composed: true }),
    );
    if (this.accountSettingsPath) {
      this._navigate(this.accountSettingsPath);
    }
  };

  private _handleLogout = () => {
    this.dispatchEvent(
      new CustomEvent("logout", { bubbles: true, composed: true }),
    );
    Auth.signOut();
  };

  private _handleMenuItemClick(e: Event, item: MenuItem) {
    e.stopPropagation();
    // If the consumer provided a callback, call it.
    try {
      if (item && typeof item.onClick === "function") {
        item.onClick(e, item);
        this.dispatchEvent(
          new CustomEvent("menu-item-click", {
            detail: { item },
            bubbles: true,
            composed: true,
          }),
        );
        return;
      }

      // Otherwise navigate to href if present
      if (item && item.href) {
        this._navigate(item.href);
        this.dispatchEvent(
          new CustomEvent("menu-item-click", {
            detail: { item },
            bubbles: true,
            composed: true,
          }),
        );
        return;
      }
    } catch (err) {
      console.error("[sb-nav-user] menu item click handler failed", err);
    }
  }
}

customElements.define("sb-nav-user", SbNavUser);
